import { AddCircleOutline } from '@mui/icons-material';
import HttpsIcon from '@mui/icons-material/Https';
import { Box, Typography, Button as MuiButton } from '@mui/material';
import React, { useState } from 'react';
import { PREMIUM_FEATURES_URL } from 'shared/constants/externalUrls';

import CreateTeamIcon from '@/assets/svg/CreateTeamIcon';
import { BlockedPremiumFeatureModal, Button } from '@/components';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import { useIsPremiumFeatureEnabled } from '@/hooks/useIsPremiumFeatureEnabled';
import { t } from '@/services/translations/config';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import { Props } from './types';

function CreateTeamInfo({ onCreateTeam }: Props) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { isPremiumFeatureEnabled } = useIsPremiumFeatureEnabled();
    const { isCurrentSubscriptionActive, currentOrganisationSubscriptionPlan } =
        useSubscriptionContext();

    const openPremiumModal = () => {
        if (!isPremiumFeatureEnabled) {
            return;
        }
        setIsModalOpen(true);
    };

    const isActiveTeamsSubscription =
        isCurrentSubscriptionActive &&
        // TODO: move this condition to separate util and use across the app
        // it is used in multiple places and it will be hard to maintain in the case of changes
        // internal trial is included here as it has 24 seats by default.
        currentOrganisationSubscriptionPlan?.seats &&
        currentOrganisationSubscriptionPlan?.seats > 1;

    const canCreateTeam =
        !isPremiumFeatureEnabled ||
        (isPremiumFeatureEnabled && isActiveTeamsSubscription);

    const handleCreateTeamPress = () => {
        if (!canCreateTeam) {
            openPremiumModal();
        } else {
            onCreateTeam();
        }
    };

    return (
        <>
            {isModalOpen && (
                <BlockedPremiumFeatureModal
                    variant="teams"
                    close={() => setIsModalOpen(false)}
                />
            )}
            <Box
                sx={{
                    borderRadius: '8px',
                    backgroundColor: '#F5F6F7',
                    display: 'flex',
                    px: '15px',
                    py: '15px',
                    width: '460px',
                    maxHeight: '314px',
                    flex: 1,
                }}
            >
                <Box>
                    <Box
                        sx={{
                            backgroundColor: ENTITY_COLOR.teams,
                            borderRadius: '8px',
                            width: '60px',
                            height: '60px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            display: 'flex',
                            pt: '5px',
                        }}
                    >
                        <CreateTeamIcon width={50} height={50} />
                    </Box>
                </Box>
                <Box
                    sx={{
                        px: '18px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        mt: '-5px',
                    }}
                >
                    <Box
                        sx={{
                            flexDirection: 'column',
                            mb: '25px',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 22,
                                fontWeight: 'bold',
                                mb: '12px',
                            }}
                        >
                            {t('CreateTeam:better_together', {
                                defaultValue: 'Better together',
                            })}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                fontWeight: 'light',
                                mb: '18px',
                                lineHeight: '24px',
                            }}
                        >
                            {t('CreateTeam:info1')}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 'light',
                                lineHeight: '22px',
                            }}
                        >
                            {t('CreateTeam:info2')}
                        </Typography>
                    </Box>
                    <Box>
                        <Button
                            color={'inherit'}
                            variant="contained"
                            caption={t('CreateTeam:create_a_team', {
                                defaultValue: 'Create a team',
                            })}
                            onClick={handleCreateTeamPress}
                            sx={{
                                backgroundColor: ENTITY_COLOR.teams,
                                opacity: canCreateTeam ? 1 : 0.3,
                                color: COLOR.whiteDarker2,
                                mr: 1,
                                '&:hover': {
                                    backgroundColor: ENTITY_COLOR.teams,
                                },
                            }}
                            startIcon={
                                canCreateTeam ? (
                                    <AddCircleOutline
                                        sx={{
                                            width: 15,
                                            height: 15,
                                            color: COLOR.whiteDarker2,
                                        }}
                                    />
                                ) : (
                                    <HttpsIcon
                                        style={{
                                            width: 16,
                                            height: 16,
                                            color: COLOR.white,
                                        }}
                                    />
                                )
                            }
                        />
                        <MuiButton
                            sx={{
                                color: ENTITY_COLOR.teams,
                                fontWeight: 700,
                                height: 37,
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={PREMIUM_FEATURES_URL}
                        >
                            {t('CreateTeam:learn_more', {
                                defaultValue: 'Learn more',
                            })}
                        </MuiButton>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default CreateTeamInfo;
